import { logger } from '@/utils/logger'
import { csrfTokenStorage } from '@/utils/csrf-token-storage'

/**
 * CSRFトークンを管理するクラス
 * バックエンドから自動的にトークンを取得し、状態変更リクエストに自動添付する
 *
 * Enhanced with cross-tab synchronization to prevent multi-tab CSRF token conflicts.
 */
export class CSRFManager {
  private refreshPromise: Promise<string> | null = null
  private readonly BASE_URL: string

  constructor() {
    this.BASE_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080/api'
  }

  /**
   * CSRFトークンを取得する（キャッシュされている場合はそれを返す）
   * Cross-tab synchronized to prevent token conflicts
   */
  async getToken(): Promise<string> {
    // First check cross-tab storage
    const storedToken = csrfTokenStorage.getToken()
    if (storedToken) {
      logger.debug('CSRF token retrieved from cross-tab storage')
      return storedToken
    }

    // Check and wait for in-progress requests atomically
    const tokenRequestState = csrfTokenStorage.getTokenRequestState()
    if (tokenRequestState.promise && tokenRequestState.inProgress) {
      logger.debug('Waiting for CSRF token fetch from another tab')
      try {
        const token = await tokenRequestState.promise
        if (token) {
          return token
        }
      } catch (error) {
        logger.debug('Token fetch from another tab failed, will retry locally', {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    // Check if another tab has the token
    const tokenFromTabs = await csrfTokenStorage.requestTokenFromTabs()
    if (tokenFromTabs) {
      logger.debug('CSRF token received from another tab')
      return tokenFromTabs
    }

    // 同時に複数回呼び出された場合は、同じPromiseを返す
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    // Mark that this tab is fetching
    csrfTokenStorage.setTokenRequestInProgress(true)

    this.refreshPromise = this.refreshTokenInternal()
    csrfTokenStorage.setTokenRequestPromise(this.refreshPromise)

    try {
      const token = await this.refreshPromise
      return token
    } finally {
      this.refreshPromise = null
      csrfTokenStorage.setTokenRequestInProgress(false)
      csrfTokenStorage.setTokenRequestPromise(null)
    }
  }

  /**
   * CSRFトークンを強制的に再取得する
   */
  async refreshToken(): Promise<string> {
    csrfTokenStorage.clearToken()
    this.refreshPromise = null
    return this.getToken()
  }

  /**
   * 内部的にCSRFトークンを取得する
   */
  private async refreshTokenInternal(): Promise<string> {
    try {
      logger.debug('CSRF token refresh started')

      // Use dedicated authentication-free CSRF token endpoint
      const response = await fetch(`${this.BASE_URL}/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include', // Session maintenance required
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        // Special handling for rate limit errors
        if (response.status === 429) {
          throw new Error('Too many CSRF token requests. Please wait and try again.')
        }

        logger.error('CSRF token fetch failed', {
          status: response.status,
          statusText: response.statusText,
          url: `${this.BASE_URL}/auth/csrf-token`,
        })
        throw new Error(`Failed to fetch CSRF token: ${response.status} ${response.statusText}`)
      }

      // Get token from JSON response or header
      const data = await response.json()
      const csrfToken = data.token || response.headers.get('X-CSRF-Token')

      if (!csrfToken) {
        throw new Error('CSRF token not found in response')
      }

      // Store in cross-tab storage
      csrfTokenStorage.setToken(csrfToken)
      logger.debug('CSRF token refresh successful and stored', { tokenLength: csrfToken.length })

      return csrfToken
    } catch (error) {
      logger.error('CSRF token refresh failed:', error)
      throw error
    }
  }

  /**
   * 状態変更リクエスト用のヘッダーを生成する
   */
  async getSecurityHeaders(
    includeAuth: boolean = true,
    authToken?: string
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': await this.getToken(),
    }

    if (includeAuth && authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    return headers
  }

  /**
   * CSRFトークンをクリアする（ログアウト時など）
   */
  clearToken(): void {
    csrfTokenStorage.clearToken()
    this.refreshPromise = null
    logger.debug('CSRF token cleared')
  }

  /**
   * fetch APIのラッパー（CSRF対応）
   */
  async secureFetch(
    url: string,
    options: RequestInit & { authToken?: string } = {}
  ): Promise<Response> {
    const { authToken, ...fetchOptions } = options
    const method = options.method?.toUpperCase() || 'GET'

    // 状態変更メソッドの場合はCSRFトークンを追加
    const isStateChangingMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)

    let headers = { ...fetchOptions.headers } as Record<string, string>

    if (isStateChangingMethod) {
      const securityHeaders = await this.getSecurityHeaders(true, authToken)
      headers = { ...headers, ...securityHeaders }
    } else if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    return fetch(url, {
      ...fetchOptions,
      credentials: 'include', // セッション維持のため必須
      headers,
    })
  }
}

// シングルトンインスタンス
export const csrfManager = new CSRFManager()
